import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, AdminConsentCallbackResponse, CONSENT_LINK_EXPIRED } from '@/lib/api';
import {
  ButtonLink,
  DiffList,
  IconArrowRight,
  IconCheck,
  InlineNotification,
  Spinner,
  type DiffRow,
} from '@/components/carbon';
import { FrameNote, PUBLIC_LINK, SignInFrame } from '@/components/PublicLayout';
import { NextSteps } from './signup/SignUpSuccess';

type CallbackStatus = 'processing' | 'success' | 'error' | 'expired';

/** How long a consent link's state lasts (TenantConfigurationService.AdminConsentStateLifetimeMinutes). */
const CONSENT_LINK_MINUTES = 15;

const AdminConsentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [result, setResult] = useState<AdminConsentCallbackResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      // Parse OAuth callback parameters from URL
      const tenantId = searchParams.get('tenant');
      const state = searchParams.get('state');
      const adminConsent = searchParams.get('admin_consent');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check if this is an error response from Azure AD
      // AADSTS650051 is a known Azure AD race condition where consent succeeds but Azure
      // reports "service principal name already present". We send it to the backend for
      // Graph API verification instead of showing an error immediately.
      const isRecoverableError = errorDescription?.includes('AADSTS650051');

      if (error && !isRecoverableError) {
        setStatus('error');
        setErrorMessage(errorDescription || error);
        return;
      }

      // Validate required parameters
      if (!state) {
        setStatus('error');
        setErrorMessage('Required details are missing from the consent link. Start the consent process again.');
        return;
      }

      try {
        // Send callback data to backend for verification
        const response = await api.processAdminConsentCallback({
          tenantId,
          state,
          adminConsent: adminConsent || undefined,
          error: error || undefined,
          errorDescription: errorDescription || undefined
        });

        if (response.success) {
          setStatus('success');
          setResult(response);
        } else if (response.errorCode === CONSENT_LINK_EXPIRED) {
          setStatus('expired');
        } else {
          setStatus('error');
          setErrorMessage(response.errorMessage || 'Admin consent verification failed');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to process admin consent callback');
      }
    };

    processCallback();
  }, [searchParams]);

  if (status === 'success' && result) return <AdminConsentSuccess result={result} />;
  if (status === 'expired') return <AdminConsentLinkExpired />;
  if (status === 'error') return <AdminConsentError message={errorMessage} />;
  return <AdminConsentProcessing />;
};

/** Consent verification in flight: a live status line in the 04 frame. */
export function AdminConsentProcessing() {
  return (
    <SignInFrame
      headline="Checking your consent with Microsoft."
      lede="Guardian confirms the grant, then shows you what happens next."
    >
      <p role="status" className="m-0 flex items-center gap-3 text-body text-text-primary">
        <Spinner size={20} />
        This takes a few moments.
      </p>
    </SignInFrame>
  );
}

/**
 * The verified-consent page (mockup carbon/04 frame). Its data arrives from a
 * POST the visual-QA harness cannot reach (blueprint §3.4.1), so it is a pure
 * component with its own SSR render test.
 */
export function AdminConsentSuccess({ result }: { result: AdminConsentCallbackResponse }) {
  const rows: DiffRow[] = [];
  if (result.tenantName) rows.push({ label: 'Organization', value: <span className="text-body text-text-primary">{result.tenantName}</span> });
  rows.push({
    label: 'Status',
    value: (
      <span className="inline-flex items-center gap-2 text-body text-text-primary">
        <IconCheck />
        Permissions granted
      </span>
    ),
  });
  if (result.permissionsGranted !== undefined) {
    rows.push({
      label: 'API permissions',
      value: <span className="text-body tabular-nums text-text-primary">{result.permissionsGranted} granted</span>,
    });
  }

  return (
    <SignInFrame
      headline="Sign in to connect your tenant."
      lede={
        result.message ??
        'Your organization has been submitted for review. Sign in to Guardian to connect your tenant the moment your access is ready.'
      }
    >
      <DiffList rows={rows} />

      {/* Self-serve connect flow (born-Hybrid): the per-tenant setup script is
          rendered in-app behind sign-in, so the customer continues into
          Guardian rather than downloading a static script here. */}
      <NextSteps />

      <div className="mt-8">
        <ButtonLink to="/" kind="primary" icon={<IconArrowRight />}>
          Continue to Guardian
        </ButtonLink>
      </div>

      <FrameNote>
        <p className="m-0">
          Questions?{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
        </p>
      </FrameNote>
    </SignInFrame>
  );
}

/**
 * OB-05: the link's state outlived its 15 minutes. Microsoft only sends the
 * admin back here without an error after they accepted, so the consent is in
 * place and Guardian just didn't record it. Says how it gets picked up (the
 * setup verify re-checks consent with Microsoft) instead of "try again".
 */
export function AdminConsentLinkExpired() {
  return (
    <SignInFrame
      headline="Tell your iuvo contact you accepted."
      lede={`Microsoft took your consent, but this link expired before Guardian could record it. Consent links work for ${CONSENT_LINK_MINUTES} minutes after they're created.`}
    >
      <InlineNotification kind="info" title="You don't need a new link.">
        Guardian records your consent when your setup script is verified.
      </InlineNotification>

      <h2 className="m-0 mt-8 text-body font-semibold text-text-primary">What happens next</h2>
      <ul className="m-0 mt-2 list-disc space-y-1 pl-5 text-body text-text-secondary marker:text-text-helper">
        <li>Your iuvo Guardian contact can record the consent sooner from their side.</li>
        <li>If you closed Microsoft&rsquo;s screen without accepting, ask them for a new link.</li>
      </ul>

      <FrameNote>
        <p className="m-0">
          Need help?{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
        </p>
      </FrameNote>
    </SignInFrame>
  );
}

/** Consent could not be verified: the blocking error, then what to try. */
export function AdminConsentError({ message }: { message: string | null }) {
  return (
    <SignInFrame headline="Request a new consent link.">
      <InlineNotification kind="error" title="Setup could not be completed.">
        {message}
      </InlineNotification>

      <h2 className="m-0 mt-8 text-body font-semibold text-text-primary">What you can try</h2>
      <ul className="m-0 mt-2 list-disc space-y-1 pl-5 text-body text-text-secondary marker:text-text-helper">
        <li>Sign in as a Global Administrator in your organization.</li>
        <li>Check the link&rsquo;s age: a consent link is valid for {CONSENT_LINK_MINUTES} minutes.</li>
        <li>Ask your iuvo Guardian contact for a new consent link.</li>
      </ul>

      <FrameNote>
        <p className="m-0">
          Need help?{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
        </p>
      </FrameNote>
    </SignInFrame>
  );
}

export default AdminConsentCallback;
